import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { shouldFilterUsername } from "../../helpers/util_helper";
import getUserRole from "../../helpers/jwt-token-access/userRole";
import { ACCOUNT_DETAIL_FONT_SX } from "../AccountDetailPolicy/accountDetailTypography";
import {
  EditAccountSortableHeader,
  EditAccountTablePagination,
  getDateSortValue,
} from "../AccountDetailPolicy/editAccountTableControls";
import {
  AccountDetailTooltip,
  TruncatedTooltipText,
} from "../AccountDetailPolicy/editAccountTooltipStyles";

const allowedRoles = ["PowerUser", "Administrator"];

const CELL_TEXT = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "#334155",
  lineHeight: 1.4,
};

const HEADER_CELL_SX = {
  ...CELL_TEXT,
  fontWeight: 600,
  fontSize: "0.6875rem",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#64748b",
  py: 1.5,
  px: 1,
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#fff",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 2,
};

const BODY_CELL_SX = {
  ...CELL_TEXT,
  py: 1.5,
  px: 1,
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
  overflow: "hidden",
};

const ACTION_BTN_SX = {
  width: 28,
  height: 28,
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#fff",
  color: "#64748b",
  padding: 0,
  "&:hover": {
    backgroundColor: "#f1f5f9",
    color: "#334155",
  },
  "&.Mui-disabled": {
    color: "#cbd5e1",
    borderColor: "#f1f5f9",
    backgroundColor: "#f8fafc",
  },
};

const DELETE_BTN_SX = {
  ...ACTION_BTN_SX,
  color: "#dc2626",
  borderColor: "#fecaca",
  backgroundColor: "#fef2f2",
  "&:hover": {
    backgroundColor: "#fee2e2",
    color: "#991b1b",
  },
  "&.Mui-disabled": {
    color: "#fca5a5",
    borderColor: "#fee2e2",
    backgroundColor: "#fff",
  },
};

/** Centered columns — compact cols get less width; Action keeps room for all controls. */
const COLUMN_HEADERS = [
  { label: "Name", field: "name", align: "center", width: "12%" },
  { label: "User Name", field: "username", align: "center", width: "14%" },
  { label: "Phone", field: "phoneNumber", align: "center", width: "11%" },
  { label: "Country", field: "country", align: "center", width: "8%" },
  { label: "Accounts", field: "accounts", align: "center", width: "6%" },
  { label: "Role", field: "roles", align: "center", width: "8%" },
  { label: "Sign-in", field: "authType", align: "center", width: "8%" },
  { label: "Created", field: "createdDate", align: "center", width: "9%" },
  { label: "Groups", field: "resourceGroup", align: "center", width: "6%" },
  { label: "Status", field: "status", align: "center", width: "8%" },
  { label: "Action", field: null, align: "center", width: "16%" },
];

const formatDisplayDate = (dateInput) => {
  if (!dateInput || dateInput === "null") return "—";
  if (typeof dateInput === "object" && dateInput.date) {
    const { year, month, day } = dateInput.date;
    const dt = new Date(year, month - 1, day);
    if (isNaN(dt.getTime())) return "—";
    return dt.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  const dt = new Date(dateInput);
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const displayValue = (value) => {
  if (value == null || value === "" || value === "null" || value === "undefined") {
    return "—";
  }
  return String(value);
};

const formatFullName = (user) => {
  const parts = [user?.firstname, user?.lastName].filter(
    (part) =>
      part != null &&
      part !== "" &&
      part !== "null" &&
      part !== "undefined"
  );
  return parts.length ? parts.join(" ") : "—";
};

const countVisibleAccounts = (user) =>
  (user?.listacct || []).filter(
    (x) => !x.accountNumber?.includes("INIT00000001")
  ).length;

const canModifyUser = (userRole, user) => {
  if (!allowedRoles.includes(userRole)) return false;
  if (userRole === "PowerUser" && `${user.roles}` === "Administrator") {
    return false;
  }
  return true;
};

const getSortValue = (user, field) => {
  switch (field) {
    case "name":
      return formatFullName(user).toLowerCase();
    case "username":
      return (user.username || "").toLowerCase();
    case "email":
      return (user.email || "").toLowerCase();
    case "phoneNumber":
      return (user.phoneNumber || "").toLowerCase();
    case "country":
      return (user.country || "").toLowerCase();
    case "accounts":
      return countVisibleAccounts(user);
    case "roles":
      return (user.roles || "").toLowerCase();
    case "authType":
      return user.authType === "SSO" ? "sso" : "legacy";
    case "createdDate":
      return getDateSortValue(user.createdDate);
    case "resourceGroup":
      return user.userGroup?.length || 0;
    case "status":
      return user.status ? 1 : 0;
    default:
      return "";
  }
};

const BadgeCount = ({ count, onClick }) => {
  const chip = (
    <Chip
      label={count}
      size="small"
      onClick={onClick}
      sx={{
        height: 22,
        minWidth: 28,
        fontSize: "0.75rem",
        fontWeight: 600,
        color: "#334155",
        backgroundColor: "#f1f5f9",
        borderRadius: "6px",
        cursor: onClick ? "pointer" : "default",
        "& .MuiChip-label": { px: 1 },
        "&:hover": onClick
          ? { backgroundColor: "#e2e8f0" }
          : undefined,
      }}
    />
  );
  return onClick ? (
    <Box component="span" sx={{ display: "inline-flex" }}>
      {chip}
    </Box>
  ) : (
    chip
  );
};

export const MyDataGrid = ({
  usersConfig,
  setaccountList,
  setModalIsOpenToTrue,
  handleEditUser,
  roleAccess,
  setShowConfirmation,
  onToggle,
  disableAddUser,
  onAddUserClick,
}) => {
  const userRole = getUserRole();
  const [toggleConfirmation, setToggleConfirmation] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");

  const handleToggleClick = (user) => {
    const action = user.status ? "disable" : "enable";
    const actionText = user.status ? "Disable" : "Enable";
    setToggleConfirmation({
      user,
      action,
      actionText,
      message: user.status
        ? "This will prevent the user from accessing the system"
        : "This will allow the user to access the system",
    });
  };

  const handleToggleConfirm = () => {
    if (toggleConfirmation) {
      onToggle({
        email: toggleConfirmation.user.email,
        action: toggleConfirmation.action,
      });
      setToggleConfirmation(null);
    }
  };

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(field);
      setOrder("asc");
    }
  };

  const filteredUsers = useMemo(() => {
    const emailDomain = process.env.REACT_APP_EMAIL_DOMAIN;
    const base = (usersConfig || []).filter(
      (user) =>
        !user.isDeleted &&
        !(emailDomain && user.email?.includes(emailDomain)) &&
        !shouldFilterUsername(user.username)
    );

    const q = searchTerm.trim().toLowerCase();
    if (!q) return base;

    return base.filter((user) => {
      const haystack = [
        formatFullName(user),
        user.firstname,
        user.lastName,
        user.username,
        user.email,
        user.phoneNumber,
        user.country,
        user.roles,
        formatDisplayDate(user.createdDate),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [usersConfig, searchTerm]);

  const sortedUsers = useMemo(() => {
    const sorted = [...filteredUsers];
    sorted.sort((a, b) => {
      const aVal = getSortValue(a, orderBy);
      const bVal = getSortValue(b, orderBy);
      if (aVal < bVal) return order === "asc" ? -1 : 1;
      if (aVal > bVal) return order === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredUsers, orderBy, order]);

  const pagedUsers = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedUsers.slice(start, start + rowsPerPage);
  }, [sortedUsers, page, rowsPerPage]);

  const addUsersTooltip = disableAddUser
    ? "Cannot create new users, increase licensing to enable more users."
    : roleAccess < 0
      ? "Admin role required!"
      : "";

  const openAccountsModal = (user) => {
    setaccountList(
      (user.listacct || []).filter(
        (x) => !x.accountNumber?.includes("INIT00000001")
      )
    );
    setModalIsOpenToTrue();
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        p: { xs: 1.5, sm: 2.5 },
        backgroundColor: "#f3f4f6",
        boxSizing: "border-box",
        ...ACCOUNT_DETAIL_FONT_SX,
      }}
    >
      <Box
        sx={{
          pb: 1.25,
          mb: 1.75,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            color: "#111827",
            fontSize: "1rem",
          }}
        >
          Users
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography
          sx={{
            color: "#64748b",
            fontSize: "0.875rem",
            fontWeight: 400,
            lineHeight: 1.5,
            flex: 1,
            minWidth: 0,
          }}
        >
          Manage organization users, roles, and account access.
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            width: { xs: "100%", sm: "auto" },
            flexShrink: 0,
          }}
        >
          <TextField
            size="small"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: { xs: "1 1 160px", sm: "0 0 auto" },
              width: { xs: "auto", sm: 220 },
              minWidth: { xs: 0, sm: 220 },
              "& .MuiOutlinedInput-root": {
                height: 40,
                fontSize: "0.875rem",
                backgroundColor: "white",
                borderRadius: "8px",
                "& fieldset": { borderColor: "#e2e8f0" },
                "&:hover fieldset": { borderColor: "#cbd5e1" },
                "&.Mui-focused fieldset": {
                  borderColor: "#111827",
                  borderWidth: "1px",
                },
              },
              "& .MuiInputBase-input": {
                py: 0,
                height: "100%",
                boxSizing: "border-box",
              },
            }}
          />
          <AccountDetailTooltip title={addUsersTooltip}>
            <Button
              variant="contained"
              disableElevation
              className="sidebar-pair-account-btn"
              onClick={onAddUserClick}
              disabled={disableAddUser}
              startIcon={<i className="mdi mdi-plus" aria-hidden="true" />}
              sx={{
                flex: { xs: "1 1 auto", sm: "0 0 auto" },
              }}
            >
              <span id="AddUsersBtn">Add Users</span>
            </Button>
          </AccountDetailTooltip>
        </Box>
      </Box>

      <Box
        sx={{
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          backgroundColor: "#fff",
          overflow: "hidden",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <TableContainer
          sx={{
            width: "100%",
            maxWidth: "100%",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            maxHeight: { xs: "none", md: "calc(100vh - 320px)" },
          }}
        >
          <Table
            size="small"
            sx={{
              width: "100%",
              minWidth: 720,
              tableLayout: "fixed",
            }}
          >
            <colgroup>
              {COLUMN_HEADERS.map((header) => (
                <col key={header.label} style={{ width: header.width }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                {COLUMN_HEADERS.map((header) =>
                  header.field ? (
                    <EditAccountSortableHeader
                      key={header.label}
                      field={header.field}
                      orderBy={orderBy}
                      order={order}
                      onSort={handleSort}
                      align={header.align}
                      headerCellSx={HEADER_CELL_SX}
                    >
                      {header.label}
                    </EditAccountSortableHeader>
                  ) : (
                    <TableCell
                      key={header.label}
                      align={header.align}
                      sx={HEADER_CELL_SX}
                    >
                      {header.label}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedUsers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_HEADERS.length}
                    align="center"
                    sx={{ ...BODY_CELL_SX, py: 6 }}
                  >
                    <Typography sx={{ ...CELL_TEXT, color: "#64748b" }}>
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pagedUsers.map((user, index) => {
                  const modifyAllowed = canModifyUser(userRole, user);
                  const accountCount = countVisibleAccounts(user);
                  const resourceGroups = user.userGroup || [];
                  const statusLabel = user.status ? "Active" : "Inactive";
                  const statusColor = user.status ? "#16a34a" : "#dc2626";
                  const statusBg = user.status ? "#ecfdf5" : "#fef2f2";

                  return (
                    <TableRow
                      key={user.email || user.username || index}
                      sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}
                    >
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        <TruncatedTooltipText sx={CELL_TEXT}>
                          {formatFullName(user)}
                        </TruncatedTooltipText>
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        <TruncatedTooltipText sx={CELL_TEXT}>
                          {displayValue(user.username)}
                        </TruncatedTooltipText>
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        <TruncatedTooltipText sx={CELL_TEXT}>
                          {displayValue(user.phoneNumber)}
                        </TruncatedTooltipText>
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        <TruncatedTooltipText sx={CELL_TEXT}>
                          {displayValue(user.country)}
                        </TruncatedTooltipText>
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        <BadgeCount
                          count={accountCount}
                          onClick={() => openAccountsModal(user)}
                        />
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        <TruncatedTooltipText sx={CELL_TEXT}>
                          {displayValue(user.roles)}
                        </TruncatedTooltipText>
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        <TruncatedTooltipText sx={CELL_TEXT}>
                          {user.authType === "SSO" ? "SSO" : "LEGACY"}
                        </TruncatedTooltipText>
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        <TruncatedTooltipText sx={CELL_TEXT}>
                          {formatDisplayDate(user.createdDate)}
                        </TruncatedTooltipText>
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        {resourceGroups.length > 0 ? (
                          <AccountDetailTooltip
                            title={resourceGroups
                              .map((g) => g.resourcename)
                              .filter(Boolean)
                              .join(", ")}
                          >
                            <span>
                              <BadgeCount count={resourceGroups.length} />
                            </span>
                          </AccountDetailTooltip>
                        ) : (
                          <Typography sx={{ ...CELL_TEXT, color: "#94a3b8" }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={BODY_CELL_SX}>
                        <AccountDetailTooltip
                          title={
                            user.status
                              ? "Active — user can access the system"
                              : "Inactive — user is locked and cannot access the system"
                          }
                        >
                          <Chip
                            label={statusLabel}
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: statusColor,
                              backgroundColor: statusBg,
                              borderRadius: "6px",
                              "& .MuiChip-label": { px: 1.25 },
                            }}
                          />
                        </AccountDetailTooltip>
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ ...BODY_CELL_SX, overflow: "visible" }}
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                            flexWrap: "nowrap",
                          }}
                        >
                          <AccountDetailTooltip
                            title={
                              roleAccess < 0
                                ? "Admin role required!"
                                : user.status
                                  ? "Disable User"
                                  : "Enable User"
                            }
                          >
                            <span>
                              <Switch
                                size="small"
                                checked={!!user.status}
                                disabled={roleAccess < 0}
                                onChange={() =>
                                  roleAccess >= 0 && handleToggleClick(user)
                                }
                                sx={{
                                  ml: 0.25,
                                  "& .MuiSwitch-switchBase.Mui-checked": {
                                    color: "#16a34a",
                                  },
                                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                    {
                                      backgroundColor: "#16a34a",
                                    },
                                }}
                              />
                            </span>
                          </AccountDetailTooltip>
                          <AccountDetailTooltip title="Modify User">
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Modify user"
                                disabled={!modifyAllowed}
                                onClick={() => handleEditUser(user)}
                                sx={ACTION_BTN_SX}
                              >
                                <EditOutlinedIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </span>
                          </AccountDetailTooltip>
                          <AccountDetailTooltip title="Delete User">
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Delete user"
                                disabled={!modifyAllowed}
                                onClick={() =>
                                  setShowConfirmation({
                                    ...user,
                                    action: "delete",
                                  })
                                }
                                sx={DELETE_BTN_SX}
                              >
                                <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </span>
                          </AccountDetailTooltip>
                          <AccountDetailTooltip title="Resend Activation Email">
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Resend activation email"
                                disabled={(user && user.status) || roleAccess < 0}
                                onClick={() =>
                                  setShowConfirmation({
                                    ...user,
                                    action: "reActive",
                                  })
                                }
                                sx={ACTION_BTN_SX}
                              >
                                <RefreshOutlinedIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </span>
                          </AccountDetailTooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <EditAccountTablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          total={sortedUsers.length}
          rowsPerPageOptions={[5, 10, 20, 30]}
          onPageChange={setPage}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(0);
          }}
        />
      </Box>

      <Dialog
        open={!!toggleConfirmation}
        onClose={() => setToggleConfirmation(null)}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: 1400 }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            width: { xs: "calc(100% - 24px)", sm: "100%" },
            m: { xs: 1.5, sm: 2 },
            overflow: "hidden",
            ...ACCOUNT_DETAIL_FONT_SX,
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            backgroundColor: "#F98125",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1.15rem",
              color: "#FFFFFF",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            {toggleConfirmation?.action === "disable"
              ? "Disable User"
              : "Enable User"}
          </Typography>
          <button
            type="button"
            aria-label="Close"
            onClick={() => setToggleConfirmation(null)}
            style={{
              padding: 0,
              margin: 0,
              border: "none",
              background: "transparent",
              color: "#FFFFFF",
              fontSize: 28,
              fontWeight: 700,
              lineHeight: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "none",
              flexShrink: 0,
            }}
          >
            <span aria-hidden="true">×</span>
          </button>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <Typography
            sx={{
              fontSize: "0.9375rem",
              fontWeight: 400,
              color: "#334155",
              lineHeight: 1.5,
              wordBreak: "break-word",
              mb: 1,
            }}
          >
            {toggleConfirmation &&
              `Are you sure you want to ${toggleConfirmation.actionText.toLowerCase()} `}
            <Box component="span" sx={{ fontWeight: 600, color: "#111827" }}>
              {toggleConfirmation?.user?.username}
            </Box>
            ?
          </Typography>
          {toggleConfirmation?.message ? (
            <Typography
              sx={{
                fontSize: "0.8125rem",
                color: "#64748b",
                lineHeight: 1.5,
                mb: 1,
              }}
            >
              {toggleConfirmation.message}
            </Typography>
          ) : null}
          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
              flexWrap: "wrap",
              gap: 1.25,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setToggleConfirmation(null)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                borderRadius: "8px",
                px: 2.5,
                color: "#475569",
                borderColor: "#cbd5e1",
                "&:hover": {
                  borderColor: "#94a3b8",
                  backgroundColor: "#f8fafc",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={handleToggleConfirm}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                borderRadius: "8px",
                px: 2.5,
                boxShadow: "none",
                backgroundColor:
                  toggleConfirmation?.action === "disable"
                    ? "#F98125"
                    : "#16a34a",
                "&:hover": {
                  backgroundColor:
                    toggleConfirmation?.action === "disable"
                      ? "#e8741c"
                      : "#15803d",
                  boxShadow: "none",
                },
              }}
            >
              {toggleConfirmation?.actionText}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MyDataGrid;
